import { useEffect } from "react";
import { useRouterState } from "@tanstack/react-router";
import {
  AnalyticsContext,
  AnalyticsScreenName,
  NavigationEvent,
} from "@salestrak/analytics-taxonomy";
import { useAnalytics } from "./analytics-provider";

/**
 * Route pattern → curated screen name. Keyed by the matched route's `fullPath`
 * BELOW `/$workspace`, with the leading/trailing slashes dropped (an index
 * route like `/$workspace/settings/access/` → `"settings/access"`). The key is
 * the route PATTERN — dynamic segments stay as their `$param` placeholders
 * ("$apiKeyId"), never the resolved value — so these keys are stable across
 * navigations and never leak entity ids, which is why the screen-name catalog
 * stays hand-curated.
 *
 * Add a new screen here when adding its route (and to AnalyticsScreenName in
 * @salestrak/analytics-taxonomy). Routes intentionally without a screen_view
 * (redirects like the workspace root `/$workspace/`, `/$workspace/settings/`,
 * `/$workspace/settings/updates` and `/$workspace/team/$teamId/`, the `$`
 * catch-all) are simply absent and silently skipped — a mapped redirect would
 * bill one visit as two screens, its own and its target's.
 */
const SCREEN_BY_ROUTE: Record<string, AnalyticsScreenName> = {
  inbox: AnalyticsScreenName.Inbox,
  map: AnalyticsScreenName.Map,
  members: AnalyticsScreenName.Members,
  teams: AnalyticsScreenName.Teams,
  workflows: AnalyticsScreenName.Workflows,
  "my-work": AnalyticsScreenName.MyWork,
  "org-chart": AnalyticsScreenName.OrgChart,
  shared: AnalyticsScreenName.SharedWithMe,
  dashboards: AnalyticsScreenName.Dashboards,
  "dashboards/my": AnalyticsScreenName.DashboardsMine,
  "dashboards/$viewId": AnalyticsScreenName.WorkspaceDashboard,
  // Workflow/team views are tabbed: `/$id` redirects to its default tab, so
  // the tracked screen lives on the explicit tab sub-routes (the bare `/$id`
  // index is a redirect and intentionally unmapped).
  "workflow/$workflowId/board": AnalyticsScreenName.WorkflowBoard,
  "workflow/$workflowId/list": AnalyticsScreenName.WorkflowList,
  "workflow/$workflowId/dashboard": AnalyticsScreenName.WorkflowDashboard,
  // One name for every custom view: the route pattern cannot tell a board
  // view from a list or dashboard view (`dashboard.open` splits dashboards).
  "workflow/$workflowId/view/$viewId": AnalyticsScreenName.WorkflowView,
  "team/$teamId/overview": AnalyticsScreenName.TeamOverview,
  "team/$teamId/board": AnalyticsScreenName.TeamBoard,
  "team/$teamId/list": AnalyticsScreenName.TeamList,
  "team/$teamId/members": AnalyticsScreenName.TeamMembers,
  "team/$teamId/chart": AnalyticsScreenName.TeamChart,
  "member/$memberId": AnalyticsScreenName.MemberDetail,
  "settings/preferences": AnalyticsScreenName.SettingsPreferences,
  "settings/appearance": AnalyticsScreenName.SettingsAppearance,
  "settings/profile": AnalyticsScreenName.SettingsProfile,
  "settings/workspace": AnalyticsScreenName.SettingsWorkspace,
  "settings/members": AnalyticsScreenName.SettingsMembers,
  "settings/members/$userId": AnalyticsScreenName.SettingsMemberDetail,
  "settings/permissions": AnalyticsScreenName.SettingsPermissions,
  "settings/teams": AnalyticsScreenName.SettingsTeams,
  "settings/teams/$teamId": AnalyticsScreenName.SettingsTeamDetail,
  "settings/custom-fields": AnalyticsScreenName.SettingsCustomFields,
  "settings/outcomes": AnalyticsScreenName.SettingsOutcomes,
  "settings/trash": AnalyticsScreenName.SettingsTrash,
  "settings/issue-sources": AnalyticsScreenName.SettingsIssueSources,
  "settings/issue-types": AnalyticsScreenName.SettingsIssueTypes,
  "settings/issue-types/$issueTypeId": AnalyticsScreenName.SettingsIssueTypeDetail,
  "settings/access": AnalyticsScreenName.SettingsAccess,
  "settings/access/create": AnalyticsScreenName.SettingsAccessKeyCreate,
  "settings/access/$apiKeyId": AnalyticsScreenName.SettingsAccessKeyDetail,
  "settings/access/$apiKeyId/edit": AnalyticsScreenName.SettingsAccessKeyEdit,
  "settings/api": AnalyticsScreenName.SettingsApi,
  "settings/api/keys/create": AnalyticsScreenName.SettingsApiKeyCreate,
  "settings/api/keys/$apiKeyId": AnalyticsScreenName.SettingsApiKeyDetail,
  "settings/api/keys/$apiKeyId/edit": AnalyticsScreenName.SettingsApiKeyEdit,
  "settings/api/webhooks/create": AnalyticsScreenName.SettingsWebhookCreate,
  "settings/api/webhooks/$webhookId": AnalyticsScreenName.SettingsWebhookDetail,
  "settings/api/webhooks/$webhookId/edit": AnalyticsScreenName.SettingsWebhookEdit,
  "settings/workflows": AnalyticsScreenName.SettingsWorkflows,
  "settings/workflows/$workflowId": AnalyticsScreenName.SettingsWorkflowDetail,
};

/**
 * Centrally tracks a screen_view whenever the active `/$workspace` route
 * changes. Mounted once in `$workspace/route.tsx` — individual screens do
 * NOT call this. Unmapped routes are skipped.
 */
export function useTrackScreenView() {
  // The deepest matched route's pattern (`/$workspace/workflow/$workflowId/board`),
  // NOT the resolved URL — resolved paths differ per entity id, so they can
  // never key the curated map above.
  const fullPath = useRouterState({
    select: (s): string => s.matches[s.matches.length - 1]?.fullPath ?? "",
  });
  const { track } = useAnalytics();

  // Key below `/$workspace`, trailing index slash dropped; the workspace
  // index route (`/$workspace/`) normalizes to "index".
  const routeKey = fullPath.startsWith("/$workspace")
    ? fullPath.slice("/$workspace".length).replace(/^\//, "").replace(/\/$/, "") ||
      "index"
    : undefined;
  const screenName = routeKey ? SCREEN_BY_ROUTE[routeKey] : undefined;

  useEffect(() => {
    if (!screenName) return;
    track({
      context: AnalyticsContext.Navigation,
      event: NavigationEvent.ScreenView,
      label: screenName,
    });
  }, [screenName, track]);
}
